import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { documentApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { sanitizeSnippetHtml } from "./sanitizeHtml";
import { sourceHost } from "./sourceHost";
import { parseSearchInput } from "./searchSyntax";
import AuthContext from "../../context/AuthContext";
import { isAgent } from "../tasks/userDisplay";
import { VISIBILITY_LABELS } from "../sharing/visibility";

const PAGE_SIZE = 12; // mirrors backend REST_FRAMEWORK PAGE_SIZE

function SourceBadge({ doc }) {
  if (doc.source_type === "url") {
    return (
      <Badge variant="outline" title={doc.source_url}>
        {sourceHost(doc.source_url) || "web page"}
      </Badge>
    );
  }
  return <Badge variant="secondary">note</Badge>;
}

SourceBadge.propTypes = {
  doc: PropTypes.shape({
    source_type: PropTypes.string,
    source_url: PropTypes.string,
  }).isRequired,
};

function EnrichmentBadge({ status }) {
  // "enriched" is the normal state — only the exceptions get a badge.
  if (status === "pending") {
    return (
      <Badge variant="outline" title="AI summary and tags on the way">
        summarizing…
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="warning" title="Open the document to retry">
        no AI summary
      </Badge>
    );
  }
  return null;
}

EnrichmentBadge.propTypes = {
  status: PropTypes.string,
};

function DocumentList() {
  // Shared library: rows can belong to the owner or any of their agents.
  const auth = useContext(AuthContext);
  const me = auth?.user?.user_id ?? null;
  const [documents, setDocuments] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = usePageParam();
  const [query, setQuery] = useState("");
  const [queryInput, setQueryInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tag slugs from `tag:` the library doesn't have — said out loud, so
  // `tag:qa` with no such tag reads "no tag called qa", not "no results"
  // (board task #29).
  const [unknownTags, setUnknownTags] = useState([]);

  const searching = Boolean(query);
  // `tag:name` filters instead of searching the word (searchSyntax.js).
  const { q: textQuery, tags: tagFilter } = parseSearchInput(query);
  const tagKey = tagFilter.join(",");

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tags = tagKey ? tagKey.split(",") : [];
      if (textQuery) {
        const response = tags.length
          ? await documentApi.search(textQuery, { tags })
          : await documentApi.search(textQuery);
        setDocuments(response.data.results || []);
        setCount((response.data.results || []).length);
        setUnknownTags(response.data.unknown_tags || []);
      } else if (tags.length) {
        // Tags alone: the (paginated) list, filtered. Search answers an
        // empty query with no results, but it still reports unknown tags.
        const [response, check] = await Promise.all([
          documentApi.getAll({ page, tags: tags.join(",") }),
          documentApi.search("", { tags }),
        ]);
        setDocuments(response.data.results || []);
        setCount(response.data.count || 0);
        setUnknownTags(check.data.unknown_tags || []);
      } else {
        const response = await documentApi.getAll({ page });
        setDocuments(response.data.results || []);
        setCount(response.data.count || 0);
        setUnknownTags([]);
      }
    } catch (err) {
      if (err?.response?.status === 404 && page > 1) {
        // stale page (the collection shrank, or an old link) — self-heal
        setPage(1, { replace: true });
        return;
      }
      setError(apiErrorMessage(err, "Failed to load documents."));
    } finally {
      setLoading(false);
    }
  }, [page, setPage, textQuery, tagKey]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setQuery(queryInput.trim());
    setPage(1, { replace: true });
  };

  const handleClear = () => {
    setQueryInput("");
    setQuery("");
    setPage(1, { replace: true });
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="page-container mt-6 pt-4">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Library"
          subtitle="Your saved notes and web pages, full-text searchable"
        />
        <Button className="shrink-0" asChild>
          <Link to="/knowledge/documents/new">Add document</Link>
        </Button>
      </div>

      <form onSubmit={handleSearchSubmit} className="mb-1">
        <div className="flex">
          <Input
            type="search"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder="Search everything you've saved…"
            aria-label="Search documents"
            className="rounded-r-none"
          />
          <Button
            type="submit"
            variant="outline"
            className={cn(
              "rounded-l-none border-l-0",
              searching && "rounded-r-none",
            )}
          >
            Search
          </Button>
          {searching && (
            <Button
              type="button"
              variant="outline"
              className="rounded-l-none border-l-0"
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </div>
      </form>
      <div className="mb-4 text-sm text-muted-foreground">
        Plain words match anywhere. Use <code>&quot;quotes&quot;</code> for
        exact phrases, <code>OR</code> for alternatives, <code>-word</code> to
        exclude, <code>tag:name</code> to show only documents with that tag.
      </div>

      {searching && tagFilter.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
          {/* Real spaces between the parts: flex gap draws the gap but
              a screen reader would read "taggedpostgres" without them. */}
          Only documents tagged{" "}
          {tagFilter.map((slug) => (
            <span key={slug}>
              <Badge variant="secondary">{slug}</Badge>{" "}
            </span>
          ))}
          {textQuery ? (
            <>
              matching <strong className="text-foreground">{textQuery}</strong>
            </>
          ) : null}
        </div>
      )}

      {!loading && unknownTags.length > 0 && (
        <Alert className="mb-3">
          <AlertDescription>
            No tag called{" "}
            {unknownTags.map((slug, i) => (
              <span key={slug}>
                {i > 0 && ", "}
                <strong>{slug}</strong>
              </span>
            ))}{" "}
            in your library — see the{" "}
            <Link
              to="/knowledge/tags"
              className="text-primary underline underline-offset-2"
            >
              Tags page
            </Link>{" "}
            for the ones that exist.
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading documents…</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button size="sm" variant="outline" onClick={fetchDocuments}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && documents.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          {searching
            ? "Nothing matches that search."
            : "Nothing saved yet. Add a note or a web page to get started."}
        </div>
      )}

      {!loading && !error && documents.length > 0 && (
        <div className="flex flex-col gap-2">
          {searching && textQuery && (
            <div className="text-sm text-muted-foreground">
              Best {documents.length} match{documents.length === 1 ? "" : "es"}{" "}
              for <strong>{textQuery}</strong>, most relevant first.
            </div>
          )}
          {documents.map((doc) => (
            <Link
              key={doc.id}
              to={`/knowledge/documents/${doc.id}`}
              className="block rounded-lg border bg-card p-4 text-card-foreground shadow-xs transition-shadow hover:shadow-md"
            >
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <h3 className="m-0 font-display text-lg font-semibold">
                  {doc.title || "Untitled"}
                </h3>
                <div className="flex shrink-0 items-center gap-2">
                  {/* The library is shared with the owner's agents, so a
                      row may not be yours. Say so — an agent's note and
                      your own should not look identical. */}
                  {doc.user && me != null && doc.user.id !== me && (
                    <span className="text-sm text-muted-foreground">
                      by {doc.user.username}
                      {isAgent(doc.user) && " (agent)"}
                      {doc.can_edit === false && " · read + copy"}
                    </span>
                  )}
                  {doc.visibility && doc.visibility !== "private" && (
                    <>
                      {" "}
                      <Badge variant="default" className="font-normal">
                        {VISIBILITY_LABELS[doc.visibility]}
                      </Badge>
                    </>
                  )}
                  <SourceBadge doc={doc} />
                  <EnrichmentBadge status={doc.enrichment_status} />
                  <span className="text-sm text-muted-foreground">
                    {new Date(doc.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {searching && doc.snippet ? (
                <p
                  className="m-0 text-muted-foreground"
                  // Server-escaped text where the only markup is the <b>
                  // match markers; sanitized again here (allow <b> only).
                  dangerouslySetInnerHTML={{
                    __html: sanitizeSnippetHtml(doc.snippet),
                  }}
                />
              ) : (
                doc.summary && (
                  <p className="m-0 text-muted-foreground">{doc.summary}</p>
                )
              )}
            </Link>
          ))}
        </div>
      )}

      {!loading && (!searching || !textQuery) && totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <Pagination page={page} total={totalPages} onChange={setPage} />
        </div>
      )}
    </div>
  );
}

export default DocumentList;
